import { ArrowUpRight, Check, Clock3, Library, GraduationCap, ListVideo, Sparkles } from 'lucide-react'

import { useCenterStore } from '@caradasstore/CenterStore'
import useEntitlementStore from '@caradasstore/EntitlementStore'
import { useUserStore } from '@caradasstore/UserStore'
import { isClientUser } from '../../utils/utils'
import { entitlementUserKey } from '../../data/entitlementUser'
import logoOnDark from '../../assets/images/adas-intelligence-horizontal-logo-dark-bg.svg'

// CAR-4024 — the approved palette: at most one filled bright-blue (#1F6FEB) button per screen;
// light blue #78C2EA marks everything else, only ever as a fill with navy text (never as text on white).
const LIGHT_BLUE = 'bg-[#78C2EA] text-foreground'
// One accent for every card, so the three paths read as one set: navy checks on a light-blue tint.
const ACCENT = { text: 'text-primary', tint: 'bg-[#78C2EA]/30', icon: 'text-foreground' }
const BUTTON = {
  primary: 'bg-primary text-primary-foreground group-hover:bg-primary-hover',
  secondary: `${LIGHT_BLUE} group-hover:bg-[#5FB2E2]`
}

// CAR-4006 — the app's how-to videos live on the ADAS Intelligence YouTube channel, one playlist
// per kind of user. Each card opens its playlist on YouTube in a new tab, outside the app. The
// playlists are unlisted, so the in-app Library is the way in; the page doesn't point at the channel.
export const TRAINING_PLAYLISTS = [
  {
    id: 'calibration-center',
    title: 'Calibration Center',
    audience: 'Owners, managers & technicians',
    description: 'Run the whole center in the app, from the AI Report to the paid invoice.',
    topics: ['Quotes and work orders', 'Scheduling and invoicing', 'AI Reports and center setup'],
    url: 'https://www.youtube.com/playlist?list=PLS629VLE-Mic',
    accent: ACCENT
  },
  {
    id: 'body-shop-clients',
    title: 'Body Shop Clients',
    audience: "Shops on a center's client portal",
    description: "See your shop's jobs at your calibration center and book appointments.",
    topics: ['Booking appointments from quotes', 'Following quotes and work orders', 'Reading AI Reports and invoices'],
    url: 'https://www.youtube.com/playlist?list=PLUIGbV_SZnwY',
    accent: ACCENT
  },
  {
    id: 'standalone-body-shop',
    title: 'Standalone Body Shop',
    audience: 'Shops using ADAS Intelligence on their own',
    description: 'Turn every repair estimate into a clear list of the calibrations it needs.',
    topics: ['Uploading estimates', 'Reading AI Reports', 'Connecting CCC'],
    url: 'https://www.youtube.com/playlist?list=PLEkJgZiLyoYc',
    accent: ACCENT
  }
]

const ALL_PLAYLIST_IDS = TRAINING_PLAYLISTS.map((p) => p.id)

// The playlists this login may see, or null while that isn't known yet. Clients get theirs (client
// wins over admin, as in the backend's RoleModulePolicy); admins get all three; a location whose plan
// is known and has no Quotes is a standalone shop (AI Brief); other centers get all three. A failed
// plan load also shows all three on purpose: the videos are unlisted, and an empty page helps no one.
const useVisiblePlaylistIds = () => {
  const user = useUserStore((s) => s.currentUser)
  const ccId = useCenterStore((s) => s.ccId)
  const loadedForCcId = useEntitlementStore((s) => s.loadedForCcId)
  const loadedForUser = useEntitlementStore((s) => s.loadedForUser)
  const planModules = useEntitlementStore((s) => s.planModules)
  const loadFailed = useEntitlementStore((s) => s.loadFailed)
  const failOpen = useEntitlementStore((s) => s.failOpen)
  if (!user) return null
  if (isClientUser(user)) return ['body-shop-clients']
  if (user.is_admin) return ALL_PLAYLIST_IDS
  if (!ccId || loadedForCcId !== ccId) return null
  // The plan is saved per user, so another user's saved answer is no answer.
  if (loadedForUser != null && loadedForUser !== entitlementUserKey(user)) return null
  if (loadFailed || failOpen || !planModules?.length) return ALL_PLAYLIST_IDS
  return planModules.includes('quotes') ? ALL_PLAYLIST_IDS : ['standalone-body-shop']
}

const Hero = ({ playlistCount }) => (
  <section
    data-testid='library-hero'
    className='bg-navy-honeycomb relative overflow-hidden rounded-card px-5 py-7 text-sidebar-foreground min-[435px]:px-8 min-[435px]:py-10'
  >
    {/* Decorative learning mark: a graduation cap, not a play button, so it doesn't suggest the video plays here. */}
    <div aria-hidden='true' className='pointer-events-none absolute right-16 top-1/2 hidden -translate-y-1/2 lg:block'>
      <div className='inline-flex h-24 w-24 items-center justify-center rounded-full bg-sidebar-foreground/10 ring-1 ring-sidebar-foreground/20 backdrop-blur-sm'>
        <GraduationCap className='h-11 w-11 text-sidebar-foreground' strokeWidth={1.5} />
      </div>
    </div>

    <div className='relative max-w-[640px]'>
      <span className='inline-flex items-center gap-1.5 rounded-full bg-sidebar-foreground/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/80 ring-1 ring-sidebar-foreground/15'>
        <Library className='h-3.5 w-3.5' />
        Library
      </span>
      <h1 className='mt-3 text-[24px] font-bold leading-tight tracking-tight min-[435px]:text-[28px]'>Learn ADAS Intelligence</h1>
      <p className='mt-2 text-[14px] leading-relaxed text-sidebar-foreground/70'>
        Short, task-by-task videos for every way you use the app. Each playlist opens on YouTube in a new tab.
      </p>
      <ul className='mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-sidebar-foreground/75'>
        {playlistCount != null && (
          <li className='inline-flex items-center gap-2'>
            <ListVideo className='h-4 w-4 text-sidebar-foreground/60' />
            {playlistCount === 1 ? '1 playlist' : `${playlistCount} playlists`}
          </li>
        )}
        <li className='inline-flex items-center gap-2'>
          <Clock3 className='h-4 w-4 text-sidebar-foreground/60' />
          Short how-to videos
        </li>
        <li className='inline-flex items-center gap-2'>
          <ArrowUpRight className='h-4 w-4 text-sidebar-foreground/60' />
          Opens on YouTube
        </li>
      </ul>
    </div>
  </section>
)

// `wide`: the only card on the page, laid out cover-left on wide screens. `primary`: this card has the
// screen's one bright-blue button (the recommended card, or the only card).
const PlaylistCard = ({ playlist, recommended, wide, primary }) => {
  const { accent } = playlist
  return (
    <a
      href={playlist.url}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={`${playlist.title} training playlist on YouTube${recommended ? ', recommended for you' : ''} (opens in a new tab)`}
      className={`group relative flex flex-col overflow-hidden rounded-card border bg-card transition duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sidebar/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        recommended ? 'border-primary shadow-lg shadow-primary/20 ring-1 ring-primary' : 'border-[#78C2EA] shadow-sm'
      } ${wide ? 'lg:max-w-[760px] lg:flex-row' : ''}`}
    >
      {/* Cover */}
      <div
        data-testid='playlist-cover'
        className={`bg-navy-honeycomb relative flex h-[148px] items-center justify-center ${wide ? 'lg:h-auto lg:w-[300px] lg:flex-none' : ''}`}
      >
        {/* The SVG draws a 1px black frame around its own edge; the clip trims it off. */}
        <img
          src={logoOnDark}
          alt=''
          aria-hidden='true'
          className='h-10 w-auto transition-transform duration-200 group-hover:scale-105'
          style={{ clipPath: 'inset(2px)' }}
        />
        <span className='absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-sidebar-foreground/10 px-2 py-1 text-[11px] font-semibold text-sidebar-foreground/80'>
          <ListVideo className='h-3.5 w-3.5' />
          YouTube playlist
        </span>
        {recommended && (
          <span
            className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${LIGHT_BLUE}`}
          >
            <Sparkles className='h-3 w-3' />
            For you
          </span>
        )}
      </div>

      {/* Body */}
      <div className='flex flex-1 flex-col px-5 pb-5 pt-4'>
        <div className={`text-[11px] font-bold uppercase tracking-[0.14em] ${accent.text}`}>{playlist.audience}</div>
        <h2 className='mt-1.5 text-[19px] font-bold tracking-tight text-foreground'>{playlist.title}</h2>
        <p className='mt-1.5 text-[13px] leading-relaxed text-foreground/70'>{playlist.description}</p>

        <ul className='mt-4 flex-1 space-y-2'>
          {playlist.topics.map((topic) => (
            <li key={topic} className='flex items-center gap-2.5 text-[13px] text-foreground'>
              <span className={`inline-flex h-5 w-5 flex-none items-center justify-center rounded-full ${accent.tint}`}>
                <Check className={`h-3 w-3 ${accent.icon}`} strokeWidth={3} />
              </span>
              {topic}
            </li>
          ))}
        </ul>

        <span
          className={`mt-5 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-bold transition-colors duration-200 ${
            primary ? BUTTON.primary : BUTTON.secondary
          }`}
        >
          Watch playlist
          <ArrowUpRight className='h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
        </span>
      </div>
    </a>
  )
}

export const AppTrainingPage = () => {
  const visibleIds = useVisiblePlaylistIds()
  const playlists = visibleIds ? TRAINING_PLAYLISTS.filter((p) => visibleIds.includes(p.id)) : []
  // With several cards, the first (Calibration Center, for the center's own staff) is marked as theirs.
  const several = playlists.length > 1

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0'>
      <Hero playlistCount={visibleIds ? playlists.length : null} />

      {!visibleIds ? (
        <p role='status' className='mt-8 text-[14px] text-foreground/60'>
          Loading your playlists…
        </p>
      ) : (
        <>
          {several && (
            <div className='mt-8'>
              <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-foreground/60'>Playlists</div>
              <div className='mt-0.5 text-[18px] font-bold text-foreground'>Choose your path</div>
              <p className='mt-1 text-[13px] text-foreground/70'>
                Your clients see Body Shop Clients; AI Brief shops see Standalone Body Shop.
              </p>
            </div>
          )}

          <div className={`grid grid-cols-1 gap-5 ${several ? 'mt-4 lg:grid-cols-3' : 'mt-8'}`}>
            {playlists.map((playlist, index) => (
              <PlaylistCard
                key={playlist.id}
                playlist={playlist}
                recommended={several && index === 0}
                wide={!several}
                primary={!several || index === 0}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
